/**
 * Faixa que convida a instalar o SimpleADM, logo depois de entrar.
 *
 * A mecânica de instalação mora em [[useInstalacao]] — aqui é só a moldura e a
 * decisão de QUANDO aparecer. Quem fechar a faixa e quiser instalar depois usa
 * o botão fixo no menu ([[BotaoInstalar]]).
 *
 * Some por 60 dias quando dispensada: aviso que volta todo dia vira parte do
 * cenário e ninguém lê.
 */
import { Download, Share, SquarePlus, X } from 'lucide-react';
import { useState } from 'react';

import { noComputador, useInstalacao } from '@/hooks/useInstalacao';

const CHAVE_ADIADO = 'simpleadm.instalar.adiado';
const DIAS_DE_SILENCIO = 60;

/** Toda leitura de `localStorage` é protegida: aba anônima com dados bloqueados LANÇA. */
function adiadoRecentemente(): boolean {
  try {
    const quando = window.localStorage.getItem(CHAVE_ADIADO);
    if (quando === null) return false;
    return Date.now() - Number(quando) < DIAS_DE_SILENCIO * 86_400_000;
  } catch {
    return false;
  }
}

function registrarAdiamento(): void {
  try {
    window.localStorage.setItem(CHAVE_ADIADO, String(Date.now()));
  } catch {
    /* Sem armazenamento, a faixa volta na próxima visita. É o pior caso, e é aceitável. */
  }
}

export function ConviteDeInstalacao() {
  const { meio, instalar } = useInstalacao();
  // Medido no primeiro render: é leitura síncrona, e o valor já está certo
  // antes da primeira pintura.
  const [adiado, setAdiado] = useState(() => adiadoRecentemente());
  const [computador] = useState(() => noComputador());

  if (adiado || meio === 'indisponivel') return null;

  const dispensar = () => {
    registrarAdiamento();
    setAdiado(true);
  };

  const aceitar = async () => {
    const escolha = await instalar();
    // Recusou? Não insiste hoje.
    if (escolha === 'dismissed') registrarAdiamento();
    setAdiado(true);
  };

  return (
    /*
     * No celular, barra presa no rodapé de ponta a ponta — `pb-[env(safe-area-inset-bottom)]`
     * mantém o botão acima da barra de gestos do iPhone, que senão come o toque. Do `lg`
     * para cima vira cartão no canto: barra de ponta a ponta em tela larga tem cara de
     * aviso de cookie, e aviso de cookie ninguém lê.
     */
    <div className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-card p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] shadow-lg lg:inset-x-auto lg:bottom-4 lg:right-4 lg:max-w-sm lg:rounded-lg lg:border lg:p-4">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 rounded-md bg-primary/10 p-2 text-primary">
          <Download className="h-4 w-4" aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-foreground">
            {computador
              ? 'Instale o Simple ADM no computador'
              : 'Deixe o Simple ADM na tela do celular'}
          </p>
          {meio === 'ios' ? (
            <p className="mt-1 flex flex-wrap items-center gap-x-1 gap-y-1 text-xs text-muted-foreground">
              <span>Toque em</span>
              <Share className="h-3.5 w-3.5" aria-hidden="true" />
              <span className="font-medium text-foreground">Compartilhar</span>
              <span>e depois em</span>
              <SquarePlus className="h-3.5 w-3.5" aria-hidden="true" />
              <span className="font-medium text-foreground">Adicionar à Tela de Início</span>.
            </p>
          ) : (
            <>
              <p className="mt-1 text-xs text-muted-foreground">
                {computador
                  ? 'Fica na área de trabalho e abre em janela própria, sem barra de endereço.'
                  : 'Abre direto, sem digitar o endereço nem entrar de novo.'}
              </p>
              <button
                type="button"
                onClick={() => void aceitar()}
                className="mt-2 flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90"
              >
                <Download className="h-4 w-4" aria-hidden="true" />
                Instalar
              </button>
            </>
          )}
        </div>
        <button
          type="button"
          onClick={dispensar}
          aria-label="Agora não"
          className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
